"use client";

import { useSupabaseClient } from "@/components/SupabaseProvider";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { TaskModel, deserializeTask } from "../models/task_model";
import { useSetInterval } from "./useSetInterval";

const TASK_POLL_INTERVAL_MS = 10000;

export const useTask = (taskId: string, polling: boolean) => {
  const supabaseClient = useSupabaseClient();

  const fetchTask = async () => {
    const { data, error } = await supabaseClient
      .from("tasks")
      .select("*")
      .eq("id", taskId)
      .single();
    if (error) throw error;
    return deserializeTask(data);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["task", taskId],
    queryFn: fetchTask,
    enabled: !!taskId,
    refetchInterval: taskId && polling ? TASK_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  return { data, isLoading, error };
};

export const useConditionalTask = (taskId: string, polling: boolean) => {
  // Always call useTask, but decide whether to actually fetch based on taskId
  const task = useTask(taskId, !!taskId && polling); // The second parameter might control fetching

  // Return task data if taskId is present, else return default state immediately
  if (!taskId) return { data: null }; // Mimic the structure of the return value from useTask

  return task;
};

export const useTaskProgress = (task: TaskModel) => {
  const getRemainingTime = () => {
    const createdAt = Date.parse(task.createdAt.toISOString());
    const projectedEndTime = createdAt + task.estimatedTimeSeconds * 1000;
    return Math.max(1, Math.max(0, projectedEndTime - Date.now()) / 1000);
  };

  const getProgressPercentage = () => {
    if (task.status != "processing") return 100;
    const createdAt = Date.parse(task.createdAt.toISOString());
    const projectedEndTime = createdAt + task.estimatedTimeSeconds * 1000;
    return Math.min(
      99,
      ((Date.now() - createdAt) / (projectedEndTime - createdAt)) * 99
    );
  };
  const [progressPercentage, setProgressPercentage] = useState(0);

  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    setRemainingTime(getRemainingTime());
    setProgressPercentage(getProgressPercentage());
  }, []);

  useSetInterval(() => {
    setRemainingTime(getRemainingTime());
    setProgressPercentage(getProgressPercentage());
  }, 1000);

  return {
    percentage: progressPercentage,
    remainingTime,
  };
};

export const useTaskFromResource = (
  resourceId: string,
  polling: boolean = true
) => {
  const supabaseClient = useSupabaseClient();

  const fetchTask = async () => {
    const { data, error } = await supabaseClient
      .from("tasks")
      .select("*")
      .eq("output_id", resourceId)
      .single();
    if (error) throw error;
    return deserializeTask(data);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["task", resourceId],
    queryFn: fetchTask,
    // enabled: !!resourceId, // This makes the build fail for some reason
    refetchInterval: resourceId && polling ? TASK_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  return { data, isLoading, error };
};
